import { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Pressable,
  TextInput,
  Image,
} from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { supabase } from '../../lib/supabase';
import { shadows } from '../../lib/styles';
import {
  ArrowRight,
  Building,
  Copy,
  Plus,
  Search,
  Trash2,
  UserCheck,
  X,
} from '../../lib/icons';

type AgentStatus = 'active' | 'inactive';

interface Agent {
  id: string;
  full_name: string;
  brokerage: string | null;
  email: string | null;
  phone_number: string | null;
  photo_url: string | null;
  website_url: string | null;
  bio: string | null;
  slug: string;
  referral_code: string;
  status: AgentStatus;
  created_at: string;
  updated_at: string;
  linked_homeowner_count?: number;
}

interface LinkedHomeowner {
  id: string;
  full_name: string | null;
  referring_agent_linked_at: string | null;
  source?: ReferralSource | null;
}

type ReferralSource = 'signup' | 'self_linked' | 'admin_assigned' | 'backfill';

interface ReferralStats {
  total: number;
  bySource: Record<ReferralSource, number>;
  lastLinkedAt: string | null;
}

const REFERRAL_SOURCE_LABELS: Record<ReferralSource, string> = {
  signup: 'Signup',
  self_linked: 'Self-linked',
  admin_assigned: 'Admin assigned',
  backfill: 'Backfill',
};

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const generateReferralCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = 'AGENT-';
  for (let i = 0; i < 6; i += 1) {
    out += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return out;
};

const buildLandingUrl = (slug: string): string => {
  if (typeof window === 'undefined') return `/agent/${slug}`;
  return `${window.location.origin}/agent/${slug}`;
};

const copyToClipboard = async (value: string): Promise<boolean> => {
  try {
    if (Platform.OS === 'web' && navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (err) {
    console.error('Clipboard write failed:', err);
  }
  return false;
};

const emptyDraft = (): Partial<Agent> => ({
  full_name: '',
  brokerage: '',
  email: '',
  phone_number: '',
  photo_url: '',
  website_url: '',
  bio: '',
  slug: '',
  referral_code: generateReferralCode(),
  status: 'active',
});

const AgentsTab = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<Agent>>(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [linkedHomeowners, setLinkedHomeowners] = useState<LinkedHomeowner[]>([]);
  const [loadingLinkedHomeowners, setLoadingLinkedHomeowners] = useState(false);
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    void fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: agentRows, error: agentError } = await supabase
        .from('real_estate_agents')
        .select('*')
        .order('created_at', { ascending: false });
      if (agentError) throw agentError;

      // Aggregate linked homeowner counts in one query.
      const ids = (agentRows || []).map((a) => a.id);
      let counts: Record<string, number> = {};
      if (ids.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('referring_agent_id')
          .in('referring_agent_id', ids);
        counts = (profiles || []).reduce<Record<string, number>>((acc, row) => {
          if (row.referring_agent_id) {
            acc[row.referring_agent_id] = (acc[row.referring_agent_id] || 0) + 1;
          }
          return acc;
        }, {});
      }

      setAgents(
        (agentRows || []).map((a) => ({
          ...(a as Agent),
          linked_homeowner_count: counts[a.id] || 0,
        }))
      );
    } catch (err: any) {
      console.error('Error fetching agents:', err);
      setError(err?.message || 'Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedHomeowners = async (agentId: string) => {
    try {
      setLoadingLinkedHomeowners(true);

      const [profileRes, auditRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, full_name, referring_agent_linked_at')
          .eq('referring_agent_id', agentId)
          .order('referring_agent_linked_at', { ascending: false }),
        supabase
          .from('real_estate_agent_referrals')
          .select('homeowner_id, source, created_at')
          .eq('agent_id', agentId)
          .order('created_at', { ascending: false }),
      ]);

      if (profileRes.error) throw profileRes.error;
      if (auditRes.error) console.warn('Could not load referral audit rows:', auditRes.error);

      const auditBySource = ((auditRes.data || []) as Array<{
        homeowner_id: string;
        source: ReferralSource | null;
        created_at: string;
      }>).reduce<Record<string, ReferralSource | null>>((acc, row) => {
        if (!acc[row.homeowner_id]) acc[row.homeowner_id] = row.source ?? null;
        return acc;
      }, {});

      const merged = ((profileRes.data || []) as LinkedHomeowner[]).map((row) => ({
        ...row,
        source: auditBySource[row.id] ?? null,
      }));
      setLinkedHomeowners(merged);

      const bySource: Record<ReferralSource, number> = {
        signup: 0,
        self_linked: 0,
        admin_assigned: 0,
        backfill: 0,
      };
      let lastLinkedAt: string | null = null;
      for (const row of (auditRes.data || []) as Array<{
        source: ReferralSource | null;
        created_at: string;
      }>) {
        if (row.source && bySource[row.source] !== undefined) {
          bySource[row.source] += 1;
        }
        if (!lastLinkedAt || (row.created_at && row.created_at > lastLinkedAt)) {
          lastLinkedAt = row.created_at;
        }
      }
      setReferralStats({
        total: (auditRes.data || []).length,
        bySource,
        lastLinkedAt,
      });
    } catch (err) {
      console.error('Error fetching linked homeowners:', err);
      setLinkedHomeowners([]);
      setReferralStats(null);
    } finally {
      setLoadingLinkedHomeowners(false);
    }
  };

  const filteredAgents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) => {
      return (
        a.full_name.toLowerCase().includes(q) ||
        (a.brokerage || '').toLowerCase().includes(q) ||
        (a.email || '').toLowerCase().includes(q) ||
        a.slug.toLowerCase().includes(q) ||
        a.referral_code.toLowerCase().includes(q)
      );
    });
  }, [agents, searchQuery]);

  const handleNewAgent = () => {
    setSelectedAgent(null);
    setDraft(emptyDraft());
    setEditing(true);
    setLinkedHomeowners([]);
  };

  const handleSelectAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    setDraft(agent);
    setEditing(false);
    void fetchLinkedHomeowners(agent.id);
  };

  const handleClose = () => {
    setSelectedAgent(null);
    setEditing(false);
    setDraft(emptyDraft());
    setLinkedHomeowners([]);
  };

  const updateDraft = <K extends keyof Agent>(key: K, value: Agent[K] | string) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value as any };
      // Auto-suggest slug from name only when creating and slug is blank.
      if (key === 'full_name' && !selectedAgent && !prev.slug) {
        next.slug = slugify(String(value));
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!draft.full_name || !draft.full_name.trim()) {
      setError('Agent full name is required');
      return;
    }
    const slug = (draft.slug || slugify(draft.full_name)).trim();
    const referralCode = (draft.referral_code || generateReferralCode()).trim().toUpperCase();
    if (!slug) {
      setError('Slug is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        full_name: draft.full_name.trim(),
        brokerage: draft.brokerage?.trim() || null,
        email: draft.email?.trim() || null,
        phone_number: draft.phone_number?.trim() || null,
        photo_url: draft.photo_url?.trim() || null,
        website_url: draft.website_url?.trim() || null,
        bio: draft.bio?.trim() || null,
        slug,
        referral_code: referralCode,
        status: (draft.status as AgentStatus) || 'active',
      };

      if (selectedAgent) {
        const { data, error: updateErr } = await supabase
          .from('real_estate_agents')
          .update(payload)
          .eq('id', selectedAgent.id)
          .select()
          .single();
        if (updateErr) throw updateErr;
        setSelectedAgent(data as Agent);
        setDraft(data as Agent);
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        const { data, error: insertErr } = await supabase
          .from('real_estate_agents')
          .insert({ ...payload, created_by: user?.id ?? null })
          .select()
          .single();
        if (insertErr) throw insertErr;
        setSelectedAgent(data as Agent);
        setDraft(data as Agent);
      }

      setEditing(false);
      await fetchAgents();
    } catch (err: any) {
      console.error('Error saving agent:', err);
      setError(err?.message || 'Failed to save agent');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAgent) return;
    if (typeof window !== 'undefined' && !window.confirm(`Delete agent "${selectedAgent.full_name}"? Linked homeowners will be unlinked but their accounts remain.`)) {
      return;
    }
    try {
      setSaving(true);
      const { error: delErr } = await supabase
        .from('real_estate_agents')
        .delete()
        .eq('id', selectedAgent.id);
      if (delErr) throw delErr;
      handleClose();
      await fetchAgents();
    } catch (err: any) {
      console.error('Error deleting agent:', err);
      setError(err?.message || 'Failed to delete agent');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateCode = () => {
    setDraft((prev) => ({ ...prev, referral_code: generateReferralCode() }));
  };

  const handleCopy = async (value: string, field: string) => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopiedField(field);
      setTimeout(() => setCopiedField((current) => (current === field ? null : current)), 1500);
    }
  };

  const renderListItem = (agent: Agent) => {
    const isSelected = selectedAgent?.id === agent.id;
    return (
      <Pressable
        key={agent.id}
        onPress={() => handleSelectAgent(agent)}
        style={({ pressed }) => [
          styles.agentRow,
          isSelected && styles.agentRowSelected,
          pressed && !isSelected && styles.agentRowPressed,
        ]}
      >
        {agent.photo_url ? (
          <Image source={{ uri: agent.photo_url }} style={styles.agentAvatar} />
        ) : (
          <View style={[styles.agentAvatar, styles.agentAvatarFallback]}>
            <Text style={styles.agentAvatarFallbackText}>
              {agent.full_name.charAt(0).toUpperCase()}
            </Text>
          </View>
        )}
        <View style={styles.agentRowInfo}>
          <View style={styles.agentRowHeader}>
            <Text style={styles.agentRowName} numberOfLines={1}>
              {agent.full_name}
            </Text>
            {agent.status === 'inactive' && (
              <View style={styles.inactiveBadge}>
                <Text style={styles.inactiveBadgeText}>Inactive</Text>
              </View>
            )}
          </View>
          {agent.brokerage ? (
            <Text style={styles.agentRowBrokerage} numberOfLines={1}>
              {agent.brokerage}
            </Text>
          ) : null}
          <Text style={styles.agentRowMeta}>
            {agent.linked_homeowner_count || 0} linked homeowner
            {agent.linked_homeowner_count === 1 ? '' : 's'}
            {' • '}
            {agent.referral_code}
          </Text>
        </View>
      </Pressable>
    );
  };

  const renderDetail = () => {
    const isCreating = !selectedAgent;
    const showEditor = editing || isCreating;
    const landingUrl = draft.slug ? buildLandingUrl(String(draft.slug)) : '';

    return (
      <View style={styles.detailCard}>
        <View style={styles.detailHeader}>
          <Text style={styles.detailTitle}>
            {isCreating ? 'New agent' : selectedAgent?.full_name}
          </Text>
          <Pressable onPress={handleClose} style={styles.iconButton} hitSlop={8}>
            <X size={18} color="#6b7280" />
          </Pressable>
        </View>

        {!showEditor && selectedAgent ? (
          <View style={styles.detailReadonly}>
            <View style={styles.shareRow}>
              <View style={styles.shareCol}>
                <Text style={styles.shareLabel}>Landing page</Text>
                <Text style={styles.shareValue} numberOfLines={1}>
                  {buildLandingUrl(selectedAgent.slug)}
                </Text>
              </View>
              <Pressable
                style={styles.copyButton}
                onPress={() => handleCopy(buildLandingUrl(selectedAgent.slug), 'landing')}
              >
                <Copy size={14} color="#111827" />
                <Text style={styles.copyButtonText}>
                  {copiedField === 'landing' ? 'Copied' : 'Copy'}
                </Text>
              </Pressable>
            </View>
            <View style={styles.shareRow}>
              <View style={styles.shareCol}>
                <Text style={styles.shareLabel}>Referral code</Text>
                <Text style={styles.shareValue}>{selectedAgent.referral_code}</Text>
              </View>
              <Pressable
                style={styles.copyButton}
                onPress={() => handleCopy(selectedAgent.referral_code, 'code')}
              >
                <Copy size={14} color="#111827" />
                <Text style={styles.copyButtonText}>
                  {copiedField === 'code' ? 'Copied' : 'Copy'}
                </Text>
              </Pressable>
            </View>

            <View style={styles.detailFieldsGrid}>
              <DetailField label="Brokerage" value={selectedAgent.brokerage} />
              <DetailField label="Email" value={selectedAgent.email} />
              <DetailField label="Phone" value={selectedAgent.phone_number} />
              <DetailField label="Website" value={selectedAgent.website_url} />
              <DetailField label="Status" value={selectedAgent.status} />
              <DetailField
                label="Created"
                value={new Date(selectedAgent.created_at).toLocaleDateString()}
              />
            </View>

            {selectedAgent.bio ? (
              <View style={styles.bioBlock}>
                <Text style={styles.fieldLabel}>Bio</Text>
                <Text style={styles.bioText}>{selectedAgent.bio}</Text>
              </View>
            ) : null}

            {referralStats && referralStats.total > 0 ? (
              <View style={styles.statsSection}>
                <Text style={styles.linkedTitle}>Referral activity</Text>
                <View style={styles.statsGrid}>
                  <View style={styles.statTile}>
                    <Text style={styles.statValue}>{referralStats.total}</Text>
                    <Text style={styles.statLabel}>Total links</Text>
                  </View>
                  {(Object.keys(referralStats.bySource) as ReferralSource[])
                    .filter((src) => referralStats.bySource[src] > 0)
                    .map((src) => (
                      <View key={src} style={styles.statTile}>
                        <Text style={styles.statValue}>{referralStats.bySource[src]}</Text>
                        <Text style={styles.statLabel}>{REFERRAL_SOURCE_LABELS[src]}</Text>
                      </View>
                    ))}
                </View>
                {referralStats.lastLinkedAt ? (
                  <Text style={styles.statsFootnote}>
                    Last linked {new Date(referralStats.lastLinkedAt).toLocaleString()}
                  </Text>
                ) : null}
              </View>
            ) : null}

            <View style={styles.linkedSection}>
              <Text style={styles.linkedTitle}>Linked homeowners</Text>
              {loadingLinkedHomeowners ? (
                <ActivityIndicator size="small" color="#6b7280" />
              ) : linkedHomeowners.length === 0 ? (
                <Text style={styles.linkedEmpty}>No homeowners linked yet.</Text>
              ) : (
                <View style={styles.linkedList}>
                  {linkedHomeowners.map((ho) => (
                    <View key={ho.id} style={styles.linkedRow}>
                      <View style={styles.linkedNameCol}>
                        <Text style={styles.linkedName}>{ho.full_name || 'Unnamed homeowner'}</Text>
                        {ho.source ? (
                          <View style={styles.sourceBadge}>
                            <Text style={styles.sourceBadgeText}>
                              {REFERRAL_SOURCE_LABELS[ho.source]}
                            </Text>
                          </View>
                        ) : null}
                      </View>
                      {ho.referring_agent_linked_at ? (
                        <Text style={styles.linkedDate}>
                          Linked{' '}
                          {new Date(ho.referring_agent_linked_at).toLocaleDateString()}
                        </Text>
                      ) : null}
                    </View>
                  ))}
                </View>
              )}
            </View>

            <View style={styles.detailActions}>
              <Pressable
                style={[styles.button, styles.buttonSecondary]}
                onPress={() => setEditing(true)}
              >
                <Text style={styles.buttonSecondaryText}>Edit</Text>
              </Pressable>
              <Pressable
                style={[styles.button, styles.buttonDanger]}
                onPress={handleDelete}
                disabled={saving}
              >
                <Trash2 size={14} color="#b91c1c" />
                <Text style={styles.buttonDangerText}>Delete</Text>
              </Pressable>
            </View>
          </View>
        ) : (
          <View style={styles.editor}>
            <FormField
              label="Full name *"
              value={String(draft.full_name || '')}
              onChange={(v) => updateDraft('full_name', v)}
              placeholder="Jane Doe"
            />
            <View style={styles.formRow}>
              <View style={styles.formCol}>
                <FormField
                  label="Brokerage"
                  value={String(draft.brokerage || '')}
                  onChange={(v) => updateDraft('brokerage', v)}
                  placeholder="Doe Realty"
                />
              </View>
              <View style={styles.formCol}>
                <FormField
                  label="Email"
                  value={String(draft.email || '')}
                  onChange={(v) => updateDraft('email', v)}
                  placeholder="jane@doerealty.com"
                  keyboardType="email-address"
                />
              </View>
            </View>
            <View style={styles.formRow}>
              <View style={styles.formCol}>
                <FormField
                  label="Phone"
                  value={String(draft.phone_number || '')}
                  onChange={(v) => updateDraft('phone_number', v)}
                  placeholder="(555) 123-4567"
                  keyboardType="phone-pad"
                />
              </View>
              <View style={styles.formCol}>
                <FormField
                  label="Website"
                  value={String(draft.website_url || '')}
                  onChange={(v) => updateDraft('website_url', v)}
                  placeholder="https://janedoe.com"
                />
              </View>
            </View>
            <FormField
              label="Photo URL"
              value={String(draft.photo_url || '')}
              onChange={(v) => updateDraft('photo_url', v)}
              placeholder="https://…/headshot.jpg"
              helper="Paste a public URL to a headshot. We'll add upload later."
            />
            <FormField
              label="Bio"
              value={String(draft.bio || '')}
              onChange={(v) => updateDraft('bio', v)}
              placeholder="A short message that appears on the agent's landing page."
              multiline
            />

            <View style={styles.formRow}>
              <View style={styles.formCol}>
                <FormField
                  label="Slug *"
                  value={String(draft.slug || '')}
                  onChange={(v) => updateDraft('slug', slugify(v))}
                  placeholder="jane-doe"
                  helper={landingUrl ? `Landing page: ${landingUrl}` : undefined}
                />
              </View>
              <View style={styles.formCol}>
                <Text style={styles.fieldLabel}>Referral code *</Text>
                <View style={styles.codeRow}>
                  <TextInput
                    value={String(draft.referral_code || '')}
                    onChangeText={(v) => updateDraft('referral_code', v.toUpperCase())}
                    style={[styles.input, styles.codeInput]}
                    autoCapitalize="characters"
                  />
                  <Pressable
                    style={styles.regenerateButton}
                    onPress={handleRegenerateCode}
                  >
                    <Text style={styles.regenerateButtonText}>Regenerate</Text>
                  </Pressable>
                </View>
              </View>
            </View>

            <View style={styles.formRow}>
              <View style={styles.formCol}>
                <Text style={styles.fieldLabel}>Status</Text>
                <View style={styles.statusToggleRow}>
                  {(['active', 'inactive'] as AgentStatus[]).map((s) => (
                    <Pressable
                      key={s}
                      onPress={() => updateDraft('status', s)}
                      style={[
                        styles.statusToggle,
                        draft.status === s && styles.statusToggleActive,
                      ]}
                    >
                      <Text
                        style={[
                          styles.statusToggleText,
                          draft.status === s && styles.statusToggleTextActive,
                        ]}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              </View>
            </View>

            <View style={styles.detailActions}>
              <Pressable
                style={[styles.button, styles.buttonPrimary, saving && styles.buttonDisabled]}
                onPress={handleSave}
                disabled={saving}
              >
                <Text style={styles.buttonPrimaryText}>
                  {saving ? 'Saving…' : isCreating ? 'Create agent' : 'Save changes'}
                </Text>
              </Pressable>
              {!isCreating && (
                <Pressable
                  style={[styles.button, styles.buttonSecondary]}
                  onPress={() => {
                    setEditing(false);
                    if (selectedAgent) setDraft(selectedAgent);
                  }}
                  disabled={saving}
                >
                  <Text style={styles.buttonSecondaryText}>Cancel</Text>
                </Pressable>
              )}
            </View>
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.tabRoot}>
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          <UserCheck size={22} color="#0f766e" />
          <Text style={styles.headerTitle}>Real Estate Agents</Text>
        </View>
        <Pressable style={styles.newButton} onPress={handleNewAgent}>
          <Plus size={16} color="#ffffff" />
          <Text style={styles.newButtonText}>New agent</Text>
        </Pressable>
      </View>

      {error ? (
        <View style={styles.errorBanner}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}

      <View style={styles.bodyRow}>
        <View style={styles.listColumn}>
          <View style={styles.searchContainer}>
            <Search size={18} color="#6b7280" />
            <TextInput
              style={styles.searchInput}
              placeholder="Search by name, brokerage, code…"
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor="#9ca3af"
            />
          </View>

          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#9ca3af" />
            </View>
          ) : filteredAgents.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Building size={36} color="#9ca3af" />
              <Text style={styles.emptyTitle}>No agents yet</Text>
              <Text style={styles.emptyText}>
                Create the first agent record so they can share a referral link.
              </Text>
              <Pressable style={styles.emptyCta} onPress={handleNewAgent}>
                <Text style={styles.emptyCtaText}>New agent</Text>
                <ArrowRight size={14} color="#ffffff" />
              </Pressable>
            </View>
          ) : (
            <WebScrollView style={styles.listScroll}>
              <View style={styles.listInner}>
                {filteredAgents.map(renderListItem)}
              </View>
            </WebScrollView>
          )}
        </View>

        <View style={styles.detailColumn}>
          {selectedAgent || editing ? (
            <WebScrollView style={styles.detailScroll}>{renderDetail()}</WebScrollView>
          ) : (
            <View style={styles.detailEmpty}>
              <UserCheck size={48} color="#d1d5db" />
              <Text style={styles.detailEmptyTitle}>Select an agent</Text>
              <Text style={styles.detailEmptyText}>
                Choose an agent on the left to view their landing link, referral code,
                and linked homeowners.
              </Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const FormField = ({
  label,
  value,
  onChange,
  placeholder,
  helper,
  multiline,
  keyboardType,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  helper?: string;
  multiline?: boolean;
  keyboardType?: 'default' | 'email-address' | 'phone-pad';
}) => (
  <View style={styles.field}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <TextInput
      style={[styles.input, multiline && styles.inputMultiline]}
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      placeholderTextColor="#9ca3af"
      multiline={multiline}
      numberOfLines={multiline ? 4 : 1}
      keyboardType={keyboardType}
    />
    {helper ? <Text style={styles.fieldHelper}>{helper}</Text> : null}
  </View>
);

const DetailField = ({ label, value }: { label: string; value: string | null }) => (
  <View style={styles.detailField}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <Text style={styles.detailFieldValue}>{value || '—'}</Text>
  </View>
);

const styles = StyleSheet.create({
  tabRoot: {
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  newButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#000000',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
  },
  newButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  errorBanner: {
    backgroundColor: '#fee2e2',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  errorText: {
    color: '#dc2626',
    fontSize: 13,
  },
  bodyRow: {
    flex: 1,
    minHeight: 0,
    gap: 16,
    ...(Platform.OS === 'web' && { flexDirection: 'row' }),
  },
  listColumn: {
    flex: 1,
    minHeight: 0,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 12,
    ...shadows.sm,
    ...(Platform.OS === 'web' && { maxWidth: 420 }),
  },
  detailColumn: {
    flex: 2,
    minHeight: 0,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#ffffff',
    marginBottom: 12,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#111827',
    paddingVertical: 4,
  },
  loadingContainer: {
    flex: 1,
    minHeight: 200,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyContainer: {
    flex: 1,
    minHeight: 200,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginTop: 6,
  },
  emptyText: {
    fontSize: 13,
    color: '#6b7280',
    textAlign: 'center',
  },
  emptyCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
  },
  emptyCtaText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  listScroll: {
    flex: 1,
    minHeight: 0,
  },
  listInner: {
    gap: 6,
  },
  agentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  agentRowSelected: {
    backgroundColor: '#f0fdfa',
    borderColor: '#99f6e4',
  },
  agentRowPressed: {
    backgroundColor: '#f9fafb',
  },
  agentAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#e5e7eb',
  },
  agentAvatarFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  agentAvatarFallbackText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
  },
  agentRowInfo: {
    flex: 1,
    gap: 2,
  },
  agentRowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  agentRowName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  agentRowBrokerage: {
    fontSize: 13,
    color: '#6b7280',
  },
  agentRowMeta: {
    fontSize: 12,
    color: '#9ca3af',
  },
  inactiveBadge: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
  },
  inactiveBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#6b7280',
  },
  detailScroll: {
    flex: 1,
    minHeight: 0,
  },
  detailCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 20,
    gap: 16,
    ...shadows.sm,
  },
  detailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  detailTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  iconButton: {
    padding: 4,
  },
  detailEmpty: {
    flex: 1,
    minHeight: 240,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 24,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    ...shadows.sm,
  },
  detailEmptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginTop: 8,
  },
  detailEmptyText: {
    fontSize: 13,
    color: '#6b7280',
    textAlign: 'center',
    maxWidth: 360,
  },
  detailReadonly: {
    gap: 16,
  },
  shareRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#f9fafb',
    padding: 12,
    borderRadius: 10,
  },
  shareCol: {
    flex: 1,
    gap: 2,
  },
  shareLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  shareValue: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
  },
  copyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  copyButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },
  detailFieldsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  detailField: {
    flexBasis: '48%',
    flexGrow: 1,
    minWidth: 160,
    gap: 4,
  },
  detailFieldValue: {
    fontSize: 14,
    color: '#111827',
  },
  bioBlock: {
    gap: 6,
  },
  bioText: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
  linkedSection: {
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    paddingTop: 16,
    gap: 10,
  },
  linkedTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  linkedEmpty: {
    fontSize: 13,
    color: '#9ca3af',
    fontStyle: 'italic',
  },
  linkedList: {
    gap: 6,
  },
  linkedRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
    borderRadius: 8,
    backgroundColor: '#f9fafb',
  },
  linkedName: {
    fontSize: 13,
    fontWeight: '500',
    color: '#111827',
  },
  linkedDate: {
    fontSize: 12,
    color: '#6b7280',
  },
  linkedNameCol: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  sourceBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: '#e0f2fe',
  },
  sourceBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#0369a1',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  statsSection: {
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    paddingTop: 16,
    gap: 10,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  statTile: {
    backgroundColor: '#f8fafc',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minWidth: 96,
    flexGrow: 1,
    alignItems: 'flex-start',
  },
  statValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0f172a',
  },
  statLabel: {
    fontSize: 11,
    color: '#64748b',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginTop: 2,
  },
  statsFootnote: {
    fontSize: 12,
    color: '#9ca3af',
  },
  detailActions: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
    paddingTop: 8,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
  },
  buttonPrimary: {
    backgroundColor: '#000000',
  },
  buttonPrimaryText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  buttonSecondary: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  buttonSecondaryText: {
    color: '#111827',
    fontSize: 14,
    fontWeight: '600',
  },
  buttonDanger: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  buttonDangerText: {
    color: '#b91c1c',
    fontSize: 14,
    fontWeight: '600',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  editor: {
    gap: 14,
  },
  field: {
    gap: 4,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  fieldHelper: {
    fontSize: 12,
    color: '#6b7280',
  },
  input: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    backgroundColor: '#ffffff',
    color: '#111827',
  },
  inputMultiline: {
    minHeight: 96,
    textAlignVertical: 'top',
  },
  formRow: {
    gap: 12,
    ...(Platform.OS === 'web' && { flexDirection: 'row' }),
  },
  formCol: {
    flex: 1,
    gap: 4,
    minWidth: 0,
  },
  codeRow: {
    flexDirection: 'row',
    gap: 8,
  },
  codeInput: {
    flex: 1,
  },
  regenerateButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
  },
  regenerateButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },
  statusToggleRow: {
    flexDirection: 'row',
    gap: 6,
  },
  statusToggle: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: '#f3f4f6',
  },
  statusToggleActive: {
    backgroundColor: '#0f766e',
  },
  statusToggleText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
  },
  statusToggleTextActive: {
    color: '#ffffff',
  },
});

export default AgentsTab;
